import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import type { CustomEnum } from '@/types/organisation';
import { ProfessionalRole, resolveProfessionalRoleLabel } from '@/types/employee';
import { useEffectiveSettings } from './useEffectiveSettings';

const EMPTY_ENUMS: CustomEnum[] = [];

export interface ProfessionalRoleOption {
  value: string;
  label: string;
}

/**
 * Raw effective `employees.role` enum rows (system + org scope) for resolving
 * professional-role labels (issue #1668). Inactive rows are included on
 * purpose: a contract saved with a role that was deactivated later must still
 * render its label. Reuses the `['settings', 'effective']` query, so the
 * Customizations mutations' existing invalidation refreshes it.
 *
 * `options` are those rows sorted by `sort_order` with resolved labels, for
 * read-only role columns and filters; while the rows load they fall back to
 * the 15 static system roles.
 */
export function useProfessionalRoleEnums(): {
  enums: CustomEnum[];
  options: ProfessionalRoleOption[];
  isLoading: boolean;
} {
  const { i18n } = useLingui();
  const { data, isLoading } = useEffectiveSettings();

  const enums = useMemo(() => data?.enums?.employees?.role ?? EMPTY_ENUMS, [data]);

  const options = useMemo(
    () =>
      enums.length === 0
        ? Object.values(ProfessionalRole).map((value) => ({
            value,
            label: resolveProfessionalRoleLabel(value, i18n, enums),
          }))
        : [...enums]
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((e) => ({
              value: e.enum_value ?? '',
              label: resolveProfessionalRoleLabel(e.enum_value, i18n, enums),
            })),
    // Key on i18n.locale: the i18n instance is stable across locale switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enums, i18n.locale]
  );

  return { enums, options, isLoading };
}
